import { useEffect } from 'react';

const BASE_URL = 'https://shopperden.com';

/**
 * Inject/update <title>, meta description, OG tags, Twitter card, canonical link
 * and (optionally) JSON-LD structured data for the current page.
 *
 * `schema` may be a single object or an array of objects — each becomes its own
 * <script type="application/ld+json"> tag, which is how Rightmove / 99acres etc.
 * achieve rich Google results (review stars, breadcrumbs, FAQ accordions).
 */
export const useSEO = ({ title, description, canonical, type = 'website', image, schema }) => {
  useEffect(() => {
    if (title) {
      document.title = `${title} | Shopperden`;
    }

    const setMeta = (selector, content) => {
      const el = document.querySelector(selector);
      if (el && content) el.setAttribute('content', content);
    };

    setMeta('meta[name="description"]', description);
    setMeta('meta[property="og:title"]', title);
    setMeta('meta[property="og:description"]', description);
    setMeta('meta[property="og:type"]', type);
    setMeta('meta[property="og:url"]', canonical);
    setMeta('meta[property="og:site_name"]', 'Shopperden');
    setMeta('meta[property="og:locale"]', 'en_GB');
    setMeta('meta[name="twitter:card"]', image ? 'summary_large_image' : 'summary');
    setMeta('meta[name="twitter:site"]', '@shopperdenuk');
    setMeta('meta[name="twitter:creator"]', '@shopperdenuk');
    if (image) {
      setMeta('meta[property="og:image"]', image);
      setMeta('meta[property="og:image:width"]', '1200');
      setMeta('meta[property="og:image:height"]', '630');
      setMeta('meta[property="og:image:alt"]', title || 'Shopperden');
    }
    setMeta('meta[name="twitter:title"]', title);
    setMeta('meta[name="twitter:description"]', description);
    if (image) setMeta('meta[name="twitter:image"]', image);

    let link = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      if (!link) {
        link = document.createElement('link');
        link.rel = 'canonical';
        document.head.appendChild(link);
      }
      link.href = canonical;
    }

    // Remove any JSON-LD nodes injected by previous page
    document.querySelectorAll('script[data-seo="structured-data"]').forEach((s) => s.remove());

    if (schema) {
      const schemas = Array.isArray(schema) ? schema : [schema];
      schemas.forEach((s) => {
        if (!s) return;
        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.setAttribute('data-seo', 'structured-data');
        script.textContent = JSON.stringify(s);
        document.head.appendChild(script);
      });
    }

    return () => {
      document.querySelectorAll('script[data-seo="structured-data"]').forEach((s) => s.remove());
    };
  }, [title, description, canonical, type, image, schema]);
};

// ============================================================================
// Schema.org generators — drop the result into useSEO({ schema: ... })
// ============================================================================

/** FAQPage schema — gives you the expandable FAQ accordion in Google results. */
export const buildFaqSchema = (items = []) => {
  if (!items.length) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map((q) => ({
      '@type': 'Question',
      name: q.q,
      acceptedAnswer: { '@type': 'Answer', text: q.a },
    })),
  };
};

/** BreadcrumbList schema — gives you the "Home > Properties > Hyderabad" trail in Google. */
export const buildBreadcrumbSchema = (crumbs = []) => {
  if (!crumbs.length) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: crumbs.map((c, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: c.name,
      item: c.url?.startsWith('http') ? c.url : `${BASE_URL}${c.url || ''}`,
    })),
  };
};

/**
 * RealEstateListing / Residence schema for property detail pages.
 * Rightmove-grade: exposes price, bedrooms, location, photos to Google so they
 * appear in the "Real estate listings" rich result block.
 */
export const buildPropertySchema = (property) => {
  if (!property) return null;
  const isLand = property.property_type === 'land';
  const isCommercial = property.property_type === 'commercial';
  const baseType = isLand ? 'LandLot' : isCommercial ? 'CommercialProperty' : 'SingleFamilyResidence';

  return {
    '@context': 'https://schema.org',
    '@type': baseType,
    name: property.title,
    description: property.description,
    image: (property.images || []).slice(0, 6),
    url: `${BASE_URL}/property/${property.id || property._id}`,
    address: {
      '@type': 'PostalAddress',
      streetAddress: property.address,
      addressLocality: property.city,
      addressRegion: property.state,
      postalCode: property.zip_code,
      addressCountry: property.country || 'GB',
    },
    geo:
      property.latitude && property.longitude
        ? { '@type': 'GeoCoordinates', latitude: property.latitude, longitude: property.longitude }
        : undefined,
    numberOfRooms: !isLand && !isCommercial ? property.bedrooms : undefined,
    numberOfBedrooms: !isLand && !isCommercial ? property.bedrooms : undefined,
    numberOfBathroomsTotal: !isLand && !isCommercial ? property.bathrooms : undefined,
    floorSize:
      property.area_sqft || property.plot_area
        ? {
            '@type': 'QuantitativeValue',
            value: property.area_sqft || property.plot_area,
            unitCode: 'FTK',
          }
        : undefined,
    offers: {
      '@type': 'Offer',
      price: property.price,
      priceCurrency: (property.currency || 'GBP').toUpperCase(),
      availability: 'https://schema.org/InStock',
      url: `${BASE_URL}/property/${property.id || property._id}`,
      seller: {
        '@type': property.listed_by_agent ? 'RealEstateAgent' : 'Person',
        name: property.agent_name || property.seller_name || 'Direct seller',
      },
    },
  };
};

/** Article schema — for blog posts and guide pages. */
export const buildArticleSchema = ({ headline, description, image, author, datePublished, dateModified, url }) => ({
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline,
  description,
  image,
  author: { '@type': 'Organization', name: author || 'Shopperden Ltd' },
  publisher: {
    '@type': 'Organization',
    name: 'Shopperden Ltd',
    logo: { '@type': 'ImageObject', url: `${BASE_URL}/icon-512.svg` },
  },
  datePublished,
  dateModified: dateModified || datePublished,
  mainEntityOfPage: url,
});

/** Service schema for the AI tool landing pages. */
export const buildServiceSchema = ({ name, description, url, areaServed }) => ({
  '@context': 'https://schema.org',
  '@type': 'Service',
  name,
  description,
  url,
  provider: {
    '@type': 'Organization',
    name: 'Shopperden Ltd',
    url: BASE_URL,
  },
  areaServed: (areaServed || ['GB', 'IN', 'CA']).map((c) => ({ '@type': 'Country', name: c })),
  termsOfService: `${BASE_URL}/terms`,
});
